import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FaCirclePlus } from "react-icons/fa6"
import toast from "react-hot-toast"

import { usePostCardRequest } from "../../hooks/useCards"
import { useAddresses } from "../../hooks/useAddresses"
import InputAdvanced from "../../components/Input/InputAdvanced"
import SelectAdvanced from "../../components/SelectAdvanced/SelectAdvanced"
import AddAddressModal from "../AddAddressModal/AddAddressModal"
import useModalStore from "../../stores/ModalStore"
import Spinner from "../../components/Spinner/Spinner"
import {
    IS_VIRTUAL_CARDS_ENABLED, IS_PHYSICAL_CARDS_ENABLED, IS_CARD_DELIVERY_ENABLED,
    RAIN_E_SIGN_CONSENT, RAIN_CARD_TERMS, RAIN_PRIVACY_POLICY
} from "../../utils/cards"
import {
    PHONE_NUMBER_CODES, DEFAULT_PHONE_CODE
} from "../../utils/utils"

import "./CardRequestModal.css"

interface CardRequestModalProps {
    /** Optional default type */
    defaultCardType?: "physical" | "virtual"
}

const CardRequestModal = ({ defaultCardType = "physical" }: CardRequestModalProps) => {
    const { t, i18n } = useTranslation("common")
    
    const { data: addresses } = useAddresses()
    const { mutateAsync: postCardRequest } = usePostCardRequest()

    const getCardTypeOptions = () => {
        const options: { [key: string]: { name: string, id: string } } = {}
        
        if (IS_PHYSICAL_CARDS_ENABLED) {
            options.physical = { name: t("card_request_form.physical"), id: "physical" }
        }
        
        if (IS_VIRTUAL_CARDS_ENABLED) {
            options.virtual = { name: t("card_request_form.virtual"), id: "virtual" }
        }
        
        return options
    }

    // NOTE this is the delivery type for physical cards
    const getRequestTypeOptions = (cardType: string | null) => {
        const options: { [key: string]: { name: string, id: string } } = {}
        
        if (cardType === 'physical') {
            options.pickup = { name: t("card_request_form.pickup"), id: "pickup" }
            
            if (IS_CARD_DELIVERY_ENABLED) {
                options.delivery = { name: t("card_request_form.delivery"), id: "delivery" }
            }
        }
        
        if (cardType === 'virtual') {
            options.pickup = { name: t("card_request_form.pickup"), id: "pickup" }
        }
        
        return options
    }
    

    
    const addressOptions = addresses?.reduce((acc: { [key: string]: any }, address: any) => {
        acc[address?.id] = { name: `${address.street}, ${address.city}, ${address.country}`, id: address?.id }
        return acc
    }, {}) || {}

    const phoneCodeOptions = PHONE_NUMBER_CODES?.reduce((acc: { [key: string]: any }, code: any) => {
        acc[code.code] = { name: code.code, id: code?.code, description: code[i18n.language] }
        return acc
    }, {}) || {}

    const officeOptions = { 0: { id: "0", name: t("nemo_office_address") } }
    // const notAvailableOptions = { 0: { id: "0", name: t("not_available") } }

    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState(() => {
        const cardTypeOptions = getCardTypeOptions();
        const initialCardType = cardTypeOptions[defaultCardType]?.id || Object.values(cardTypeOptions)[0]?.id || null;
        const requestTypeOptions = initialCardType ? getRequestTypeOptions(initialCardType) : {};
        const initialRequestType = Object.values(requestTypeOptions)[0]?.id || null;
        
        return {
            card_type: initialCardType,
            type: initialRequestType,
            data: {
                phone_number: "",
                address: {
                    line_1: "",
                    city: "",
                    region: "",
                    postal_code: "",
                    country_code: "",
                    country: ""
                },
                monthly_volume: ""
            }
        }
    })

    const [phoneNumberCode, setPhoneNumberCode] = useState(DEFAULT_PHONE_CODE.code)
    const [phoneNumber, setPhoneNumber] = useState("")
    const [userAddress, setUserAddress] = useState("")
    const [terms, setTerms] = useState({
        term1: false,
        term2: false,
        term3: false,
        term4: false
    })

    const currentCardTypeOptions = getCardTypeOptions()
    const currentRequestTypeOptions = getRequestTypeOptions(formData.card_type)

    const isPhysicalCardSelected = formData.card_type === 'physical'
    const isVirtualCardSelected = formData.card_type === 'virtual'
    const isPickupSelected = formData.type === 'pickup'

    const termsAccepted = Object.values(terms).every(val => val === true)
    const isFormValid = () => {
        if (!formData.card_type) return false;
    
        if (isPhysicalCardSelected) {
            if (!formData.type) return false;
            if (!isPickupSelected && !userAddress) return false; // Address required for delivery
        }
    
        if (isVirtualCardSelected) {
            return (
                phoneNumber.trim() !== "" &&
                userAddress !== "" &&
                formData.data.monthly_volume.trim() !== "" &&
                termsAccepted
            );
        }
    
        return true;
    };

    const { setModalContent, closeModal } = useModalStore((state) => {
        return {
            setModalContent: state.setModalContent,
            closeModal: state.closeModal,
        }
    })

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleAcceptTerms = (term: keyof typeof terms) => {
        setTerms((prev) => ({
            ...prev,
            [term]: !prev[term],
        }))
    }

    // This is necessary to update the request type
    const handleCardTypeChange = (cardType: string | null) => {
        const requestTypeOptions = getRequestTypeOptions(cardType)
        const newRequestType = Object.keys(requestTypeOptions)[0] || null
        
        setFormData(prev => ({
            ...prev,
            card_type: cardType,
            type: newRequestType
        }))
    }

    const handlePhoneNumberChange = (key: "code" | "number", value: string) => {
        if (key === "code") {
            setPhoneNumberCode(value)
            setFormData((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    phone_number: value.replace(/\D/g, '') + phoneNumber
                }
            }))
        }
        if (key === "number") {
            setPhoneNumber(value)
            setFormData((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    phone_number: phoneNumberCode.replace(/\D/g, '') + value
                }
            }))
        }
    }

    const handleAddressChange = (address: string) => {
        setUserAddress(address)
        const selectedAddress = addresses?.find((addr: any) => addr.id == address) || null // int == string
        if (selectedAddress) {
            setFormData((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    address: {
                        ...prev.data.address,
                        line_1:  `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.country}`,
                        city: selectedAddress.city,
                        region: selectedAddress.state,
                        postal_code: selectedAddress.zip_code,
                        country_code: selectedAddress.country_code,
                        country: selectedAddress.country
                    }
                }
            }))
        }
    }
    
    const handleMonthlyVolumeChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                monthly_volume: value
            }
        }))
    }

    const handleAddAddressModalOpen = (defaultType: "physical" | "virtual" = "physical") => {
        setModalContent(
            <AddAddressModal 
                onClose={() => {
                    setModalContent(<CardRequestModal defaultCardType={defaultType}/>)
                }}
            />
        )
    }

    const handleSubmit = async () => {
        try {
            setIsLoading(true)
            const result = await postCardRequest({formData})
            setIsLoading(false)

            if (result.status === 201) {
                toast.dismiss()
                toast.success(t("card_request_form.success"))
                closeModal()
            }
        } catch (error) {
            setIsLoading(false)
            toast.dismiss()
            toast.error(t("card_request_form.error"))
        }
    }

    return (
        <div className="card-request-modal">
            <div className="card-request-field-container">
                {/* <div className="card-request-info">
                    {t("card_request_form.info")}
                </div> */}
                {/* Physical or virtual */}
                <div className="card-request-field">
                    <SelectAdvanced
                        label={t("card_request_form.select_card_type")}
                        value={formData?.card_type || ""}
                        items={currentCardTypeOptions}
                        image={false}
                        onChange={(selectedCardType) => handleCardTypeChange(selectedCardType)}
                    />
                </div>
                {/* Delivery type */}
                {isPhysicalCardSelected ? (
                    <div className="card-request-field">
                        <SelectAdvanced
                            label={t("card_request_form.select_type")}
                            value={formData?.type || ""}
                            items={currentRequestTypeOptions}
                            image={false}
                            onChange={(selectedType) => handleInputChange("type", selectedType)}
                        />
                    </div>
                ) : null}
                {/* Delivery address or pickup address*/}
                {isPhysicalCardSelected && !isPickupSelected ? (
                    <div className="card-request-field">
                        <SelectAdvanced
                            label={t("card_request_form.select_address")}
                            value={userAddress || ""}
                            items={addressOptions}
                            image={false}
                            onChange={(selectedAddressId) => handleAddressChange(selectedAddressId)}
                        />
                        <div onClick={() => handleAddAddressModalOpen("physical")} className="create-address-button" >
                            <FaCirclePlus
                                className="card-request-add-address-icon"
                                size={30}
                            />
                        </div>
                    </div>
                ) : isPhysicalCardSelected && officeOptions ? (
                    <div className="card-request-field">
                        {/* NOTE this is just for displaying the office address */}
                        <SelectAdvanced
                            label={t("card_request_form.select_address")}
                            value={officeOptions[0].id}
                            items={officeOptions}
                            image={false}
                            onChange={() => {}}
                        />
                    </div>
                ) : null}
                {/* TODO this should be turned into components it looks horrible */}
                {IS_VIRTUAL_CARDS_ENABLED && isVirtualCardSelected ? (
                <>
                    {/* Address of the user for card */}
                    <div className="card-request-field">
                        <SelectAdvanced
                            label={t("card_request_form.user_address")}
                            value={userAddress || ""}
                            items={addressOptions}
                            image={false}
                            onChange={(selectedAddressId) => handleAddressChange(selectedAddressId)}
                            
                        />
                        <div onClick={() => handleAddAddressModalOpen("virtual")} className="create-address-button" >
                            <FaCirclePlus
                                className="card-request-add-address-icon"
                                size={30}
                            />
                        </div>
                    </div>
                    <div className="card-request-number-field">
                        {/* Phone number */}
                       <div className="card-request-phone-code">
                            <SelectAdvanced 
                                // isSearchable={true} // TODO fix this and add it
                                label={t("card_request_form.phone_code")}
                                value={phoneNumberCode || DEFAULT_PHONE_CODE.code}
                                items={phoneCodeOptions}
                                image={false}
                                onChange={(selectedPhoneCode) => handlePhoneNumberChange("code", selectedPhoneCode)}
                            />
                        </div> 
                        <div className="card-request-phone-number">
                            <InputAdvanced
                                field={{
                                    name: t("card_request_form.phone_number"),
                                    description: t("card_request_form.phone_number_placeholder"),
                                    type: "number",
                                }}
                                onInputChange={(_: object, value: string) => {
                                    handlePhoneNumberChange("number", value)
                                    return value
                                }}
                            />
                        </div>
                    </div>
                    <div className="card-request-field">
                        <InputAdvanced
                            field={{
                                name: t("card_request_form.monthly_volume"),
                                description: t("card_request_form.monthly_volume_placeholder"),
                                type: "number",
                            }}
                            onInputChange={(_: object, value: string) => {
                                handleMonthlyVolumeChange(value)
                                return value
                            }}
                        />
                    </div>
                    <div className="checkboxes-cardrequest">
                        <div className="checkbox-main-cont-cardrequest">
                            <div onClick={() => handleAcceptTerms("term1")} className={terms.term1 ? "checkbox cont-checked" : "checkbox cont-unchecked"}>
                                <div className={terms.term1 ? "checkbox-ball ball-checked" : "checkbox-ball ball-unchecked"}></div>
                            </div>
                            <div onClick={() => handleAcceptTerms("term1")} className="checkbox-jsx-cont-cardrequest">
                                {t('card_term1_texta')}
                                <a className="document-anchor" id="anchor" target="_blank" href={RAIN_E_SIGN_CONSENT}>{t('card_term1_textb')}</a>
                            </div>
                        </div>
                        <div className="checkbox-main-cont-cardrequest">
                            <div onClick={() => handleAcceptTerms("term2")} className={terms.term2 ? "checkbox cont-checked" : "checkbox cont-unchecked"}>
                                <div className={terms.term2 ? "checkbox-ball ball-checked" : "checkbox-ball ball-unchecked"}></div>
                            </div>
                            <div onClick={() => handleAcceptTerms("term2")} className="checkbox-jsx-cont-cardrequest">
                                {t('card_term2_texta')}
                                <a className="document-anchor" id="anchor" target="_blank" href={RAIN_CARD_TERMS}>{t('card_term2_textb')}</a>
                                {t('card_term2_textc')}
                                <a className="document-anchor" id="anchor" target="_blank" href={RAIN_PRIVACY_POLICY}>{t('card_term2_textd')}</a>
                            </div>
                        </div>
                        <div className="checkbox-main-cont-cardrequest">
                            <div onClick={() => handleAcceptTerms("term3")} className={terms.term3 ? "checkbox cont-checked" : "checkbox cont-unchecked"}>
                                <div className={terms.term3 ? "checkbox-ball ball-checked" : "checkbox-ball ball-unchecked"}></div>
                            </div>
                            <div onClick={() => handleAcceptTerms("term3")} className="checkbox-jsx-cont-cardrequest">
                                {t('card_term3_texta')}
                            </div>
                        </div>
                        <div className="checkbox-main-cont-cardrequest">
                            <div onClick={() => handleAcceptTerms("term4")} className={terms.term4 ? "checkbox cont-checked" : "checkbox cont-unchecked"}>
                                <div className={terms.term4 ? "checkbox-ball ball-checked" : "checkbox-ball ball-unchecked"}></div>
                            </div>
                            <div onClick={() => handleAcceptTerms("term4")} className="checkbox-jsx-cont-cardrequest">
                                {t('card_term4_texta')}
                            </div>
                        </div>
                    </div>
                </>
                ) : null}
            </div>
            <div className="card-request-action-button-div">
                {isLoading ? (
                    <Spinner />
                ) : (
                    <>
                        <button className="card-request-submit-button" onClick={closeModal}>
                            {t("cancel")}
                        </button>
                        <button className="card-request-submit-button" disabled={!termsAccepted || !isFormValid() || (isPhysicalCardSelected && !IS_PHYSICAL_CARDS_ENABLED)} onClick={handleSubmit}>
                            {t("submit")}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

export default CardRequestModal
